import React, { useMemo, useRef } from "react";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { Search, Star, Link2, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { sigMeta, fmt, cur, pct, UP, DOWN } from "@/lib/format";
import { useWatchlist } from "@/lib/useWatchlist";
import PullToRefresh from "@/components/dashboard/PullToRefresh";
import { haptic } from "@/lib/haptics";

const UNIVERSES = [
  { key: "all", label: "ALL" },
  { key: "nifty", label: "NIFTY 50" },
  { key: "sp500", label: "S&P 500" },
  { key: "etf", label: "ETF/IDX" },
];

const FILTERS = [
  { key: "all", label: "ALL" },
  { key: "STRONG_BUY", label: "STRONG BUY" },
  { key: "STRONG_SHORT", label: "STRONG SHORT" },
  { key: "MONITOR_BUY", label: "WATCH BUY" },
  { key: "MONITOR_SHORT", label: "WATCH SHORT" },
];

const SIGNAL_RANK = { STRONG_SHORT: 0, STRONG_BUY: 1, MONITOR_SHORT: 2, MONITOR_BUY: 3, NEUTRAL: 4 };

const MiniStat = ({ label, children }) => (
  <div className="flex flex-col">
    <span className="text-[9px] uppercase tracking-wide text-slate-400">{label}</span>
    <span className="font-mono text-[13px] font-bold tabular-nums leading-tight">{children}</span>
  </div>
);

function SwipeCard({ it, starred, onToggle, onSelect, delay }) {
  const m = sigMeta(it.signal);
  const up = (it.change_pct ?? 0) >= 0;
  const dev = it.pe_dev_pct;
  const x = useMotionValue(0);
  const dragged = useRef(false);
  // Background hint opacity as the card is swiped right
  const hintOpacity = useTransform(x, [0, 40, 80], [0, 0.6, 1]);

  return (
    <div className="relative overflow-hidden rounded-[6px]">
      {/* Swipe-to-star background */}
      <motion.div
        className="absolute inset-0 flex items-center gap-2 px-5 pointer-events-none"
        style={{ opacity: hintOpacity, background: starred ? "#FEF2F2" : "#FEF9C3" }}
      >
        <Star size={20} fill={starred ? "none" : "#EAB308"} color={starred ? "#EF4444" : "#EAB308"} />
        <span className="text-[12px] font-bold tracking-wide" style={{ color: starred ? "#991B1B" : "#854D0E" }}>
          {starred ? "REMOVE FROM WATCHLIST" : "ADD TO WATCHLIST"}
        </span>
      </motion.div>

      <motion.div
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={{ left: 0.05, right: 0.6 }}
        dragDirectionLock
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay }}
        onDragStart={() => { dragged.current = false; }}
        onDrag={(e, info) => { if (Math.abs(info.offset.x) > 8) dragged.current = true; }}
        onDragEnd={(e, info) => {
          if (info.offset.x > 80) { onToggle(it.ticker); haptic(18); }
          animate(x, 0, { type: "spring", stiffness: 500, damping: 40 });
        }}
        onClick={() => { if (!dragged.current) onSelect(it.ticker); }}
        data-testid={`mobile-screener-card-${it.ticker}`}
        className="relative w-full text-left bg-white border border-[#E2E8F0] rounded-[6px] shadow-xs p-3 active:bg-[#F8FAFC] transition-colors cursor-pointer touch-pan-y"
        style={{ x, borderLeft: `3px solid ${m.color}` }}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={(e) => { e.stopPropagation(); onToggle(it.ticker); haptic(18); }}
              data-testid={`mobile-star-${it.ticker}`}
              className="shrink-0 text-slate-300 -m-1 p-1"
            >
              <Star size={17} fill={starred ? "#EAB308" : "none"} color={starred ? "#EAB308" : "currentColor"} />
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="font-bold text-[16px] text-slate-900 leading-none">{it.ticker.replace(".NS", "").replace("^", "")}</span>
                <span className="text-[9px] font-semibold text-slate-400">{it.asset_type && it.asset_type !== "STOCK" ? it.asset_type : it.market}</span>
              </div>
              <div className="text-[11px] text-slate-500 truncate max-w-[150px] mt-0.5">{it.name}</div>
            </div>
          </div>
          <span
            className="shrink-0 font-semibold text-[10px] tracking-wide px-2 py-1 rounded-[4px] border inline-flex items-center gap-1"
            style={{ color: m.color, background: m.bg, borderColor: m.border }}
          >
            <span className="text-[7px]">{m.icon}</span>{m.label}
          </span>
        </div>

        <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-[#F1F5F9]">
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-[17px] font-black text-slate-900 tabular-nums">{cur(it.market)}{fmt(it.price)}</span>
            <span className="font-mono text-[12px] font-bold tabular-nums" style={{ color: up ? UP : DOWN }}>
              {up ? "+" : ""}{fmt(it.change_pct, 1)}%
            </span>
          </div>
          <div className="flex items-center gap-4">
            <MiniStat label="RSI">
              <span style={{ color: it.rsi > 80 ? DOWN : it.rsi < 28 ? UP : "#334155" }}>{fmt(it.rsi, 0)}</span>
            </MiniStat>
            <MiniStat label="P/E">
              <span className="text-slate-600">{fmt(it.current_pe, 0)}</span>
            </MiniStat>
            <MiniStat label="5Y DEV">
              <span style={{ color: (dev ?? 0) >= 50 ? DOWN : (dev ?? 0) <= -50 ? UP : "#64748B" }}>{pct(dev, 0)}</span>
            </MiniStat>
            <ChevronRight size={16} className="text-slate-300" />
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default function MobileScreener({ items, universe, setUniverse, signalFilter, setSignalFilter, q, setQ, onSelect, loading, onRefresh }) {
  const { has, toggle, count, code, adopt } = useWatchlist();
  const [watchOnly, setWatchOnly] = React.useState(false);

  const onSync = () => {
    const entered = window.prompt(
      `Your watchlist sync code:\n\n    ${code}\n\nEnter this code on another device to load the same watchlist.\nOr paste a code here to adopt that watchlist:`,
      ""
    );
    if (entered && entered.trim()) adopt(entered);
  };

  const sorted = useMemo(() => {
    const arr = [...items];
    arr.sort((a, b) => (SIGNAL_RANK[a.signal] ?? 9) - (SIGNAL_RANK[b.signal] ?? 9));
    return arr;
  }, [items]);

  const visible = watchOnly ? sorted.filter((it) => has(it.ticker)) : sorted;

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="mobile-screener">
      {/* Sticky controls */}
      <div className="bg-white border-b border-[#E2E8F0] px-3 pt-3 pb-2.5 space-y-2.5 shrink-0">
        <div className="flex items-center justify-between">
          <span className="font-bold text-[13px] tracking-[0.1em] uppercase text-slate-700">Signal Screener</span>
          <span className="font-mono text-[11px] text-slate-400 tabular-nums">{visible.length} SYM</span>
        </div>

        <div className="relative">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search ticker / name"
            data-testid="mobile-screener-search"
            className="h-10 pl-8 bg-[#F8FAFC] border-[#CBD5E1] text-slate-800 text-[15px] placeholder:text-slate-400 focus-visible:ring-blue-500/40"
          />
        </div>

        <div className="flex gap-1 p-0.5 bg-[#F1F5F9] rounded-[4px]">
          {UNIVERSES.map((u) => (
            <button
              key={u.key}
              onClick={() => setUniverse(u.key)}
              data-testid={`mobile-universe-tab-${u.key}`}
              className={`flex-1 font-semibold text-[11px] tracking-wide py-2 rounded-[3px] transition-colors ${
                universe === u.key ? "bg-[#121D28] text-white shadow-xs" : "text-slate-500"
              }`}
            >
              {u.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar -mx-0.5 px-0.5 py-0.5">
          <button
            onClick={() => setWatchOnly((v) => !v)}
            data-testid="mobile-filter-watchlist"
            className="shrink-0 font-semibold text-[11px] tracking-wide px-2.5 py-1.5 rounded-full border inline-flex items-center gap-1"
            style={{
              background: watchOnly ? "#FEF9C3" : "#FFFFFF",
              borderColor: watchOnly ? "#FACC15" : "#E2E8F0",
              color: watchOnly ? "#854D0E" : "#64748B",
            }}
          >
            <Star size={12} fill={watchOnly ? "#EAB308" : "none"} color={watchOnly ? "#EAB308" : "currentColor"} />
            {count > 0 ? `WATCH (${count})` : "WATCH"}
          </button>
          <button
            onClick={onSync}
            data-testid="mobile-watchlist-sync"
            className="shrink-0 font-semibold text-[11px] tracking-wide px-2.5 py-1.5 rounded-full border border-[#E2E8F0] text-slate-500 inline-flex items-center gap-1"
          >
            <Link2 size={12} /> SYNC
          </button>
          <span className="w-px h-5 bg-[#E2E8F0] shrink-0" />
          {FILTERS.map((f) => {
            const active = signalFilter === f.key;
            const mm = f.key !== "all" ? sigMeta(f.key) : null;
            return (
              <button
                key={f.key}
                onClick={() => setSignalFilter(f.key)}
                data-testid={`mobile-signal-filter-${f.key}`}
                className="shrink-0 font-semibold text-[11px] tracking-wide px-2.5 py-1.5 rounded-full border transition-colors"
                style={{
                  background: active ? (mm ? mm.bg : "#121D28") : "#FFFFFF",
                  borderColor: active ? (mm ? mm.border : "#121D28") : "#E2E8F0",
                  color: active ? (mm ? mm.color : "#FFFFFF") : "#64748B",
                }}
              >
                {f.label}
              </button>
            );
          })}
        </div>
        <div className="text-[10px] text-slate-400 flex items-center gap-1"><ChevronRight size={11} /> Swipe a card right to star · pull down to refresh</div>
      </div>

      {/* Cards */}
      <PullToRefresh onRefresh={onRefresh} className="flex-1 min-h-0 p-3 space-y-2.5 bg-[#F1F5F9]" testid="mobile-screener-list">
        {loading && items.length === 0 ? (
          <div className="p-6 text-center font-mono text-[13px] text-slate-400">Scanning universe…</div>
        ) : visible.length === 0 ? (
          <div className="p-6 text-center text-[14px] text-slate-400">
            {watchOnly ? "No starred tickers yet. Tap ★ or swipe a card to add." : "No symbols match this filter."}
          </div>
        ) : (
          visible.map((it, idx) => (
            <SwipeCard
              key={it.ticker}
              it={it}
              starred={has(it.ticker)}
              onToggle={toggle}
              onSelect={onSelect}
              delay={Math.min(idx * 0.015, 0.25)}
            />
          ))
        )}
      </PullToRefresh>
    </div>
  );
}
